import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import LogoLoader from '../components/LogoLoader';
import { 
  Wallet, 
  CreditCard, 
  Plus, 
  Trash2, 
  Calendar,
  TrendingUp,
  PieChart,
  BarChart3,
  Settings,
  Receipt,
  ArrowLeft,
  DollarSign,
  Clock,
  FileText,
  FlaskConical,
  ShieldCheck,
  AlertTriangle,
  Download,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Edit2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CafeExpenseTracker = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [categories, setCategories] = useState([]);
  const [currentEnvironment, setCurrentEnvironment] = useState('dev');
  const [initialLoading, setInitialLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    cash_balance: 0,
    online_balance: 0,
    recent_expenses: [],
    today_turnover: 0,
    environment: 'dev'
  });
  const [settings, setSettings] = useState({
    opening_cash_balance: 0,
    opening_online_balance: 0
  });
  
  // Expense form state
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    description: '',
    amount: '',
    payment_mode: 'cash',
    paid_to: ''
  });
  
  // Daily Account form state
  const [dailyAccountForm, setDailyAccountForm] = useState({
    date: new Date().toISOString().split('T')[0],
    cash_collected: '',
    online_collected: '',
    expenses: []
  });
  const [isEditingDailyAccount, setIsEditingDailyAccount] = useState(false);
  const [originalEditDate, setOriginalEditDate] = useState(null); // Track original date when editing
  const [isViewMode, setIsViewMode] = useState(false); // View mode - read only until Edit is clicked
  const [showChangeDateModal, setShowChangeDateModal] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  
  // Reports state
  const [reportFilters, setReportFilters] = useState({
    start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [reportData, setReportData] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedPaymentModes, setExpandedPaymentModes] = useState({});
  const [editingExpense, setEditingExpense] = useState(null);
  const [editExpenseForm, setEditExpenseForm] = useState({
    date: '', category: '', description: '', amount: '', payment_mode: 'cash', paid_to: ''
  });
  
  // Modal states
  const [isNewCategoryOpen, setIsNewCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ opening_cash: '', opening_online: '' });
  const [isSwitchingEnv, setIsSwitchingEnv] = useState(false);
  const [showEnvConfirm, setShowEnvConfirm] = useState(false);
  const [pendingEnv, setPendingEnv] = useState(null);
  
  // Daily Account submission state
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);
  const [lastSubmittedData, setLastSubmittedData] = useState(null);

  useEffect(() => {
    fetchEnvironment();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      await Promise.all([fetchCategories(), fetchDashboard(), fetchSettings()]);
      setInitialLoading(false);
    };
    loadData();
  }, [currentEnvironment]);

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReports();
    }
  }, [activeTab, reportFilters, currentEnvironment]);

  const fetchEnvironment = async () => {
    try {
      const response = await axios.get(`${API}/cafe/environment`);
      setCurrentEnvironment(response.data.environment || 'dev');
    } catch (error) {
      console.error('Error fetching environment:', error);
    }
  };

  const handleSwitchEnvironment = async (newEnv) => {
    if (newEnv === currentEnvironment) return;
    
    if (newEnv === 'prod') {
      // Show confirmation for switching to production
      setPendingEnv(newEnv);
      setShowEnvConfirm(true);
    } else {
      // Direct switch to dev
      await confirmSwitchEnvironment(newEnv);
    }
  };

  const confirmSwitchEnvironment = async (env) => {
    setIsSwitchingEnv(true);
    try {
      await axios.put(`${API}/cafe/environment`, { environment: env });
      setCurrentEnvironment(env);
      toast.success(`Switched to ${env === 'prod' ? 'Production' : 'Development'} environment`);
      setShowEnvConfirm(false);
      setPendingEnv(null);
    } catch (error) {
      toast.error('Failed to switch environment');
    } finally {
      setIsSwitchingEnv(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/cafe/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/cafe/dashboard`);
      setDashboardData(response.data);
      if (response.data.environment) {
        setCurrentEnvironment(response.data.environment);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/cafe/settings`);
      setSettings(response.data);
      setSettingsForm({
        opening_cash: response.data.opening_cash_balance || 0,
        opening_online: response.data.opening_online_balance || 0
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/cafe/reports`, {
        params: {
          start_date: reportFilters.start_date,
          end_date: reportFilters.end_date
        }
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    try {
      await axios.post(`${API}/cafe/categories`, { name: newCategoryName.trim() });
      toast.success('Category created successfully');
      setNewCategoryName('');
      setIsNewCategoryOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleFlushData = async () => {
    if (!window.confirm('⚠️ Are you sure you want to DELETE ALL DATA in the Testing environment? This cannot be undone!')) {
      return;
    }
    try {
      await axios.delete(`${API}/cafe/environment/dev/data`);
      toast.success('All testing data has been cleared!');
      fetchDashboard();
      fetchCategories();
    } catch (error) {
      toast.error('Failed to flush data');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/cafe/settings`, {
        opening_cash_balance: parseFloat(settingsForm.opening_cash) || 0,
        opening_online_balance: parseFloat(settingsForm.opening_online) || 0
      });
      toast.success('Settings saved successfully');
      setIsSettingsOpen(false);
      fetchSettings();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.category || !expenseForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await axios.post(`${API}/cafe/expenses`, {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      });
      toast.success('Expense added successfully');
      setExpenseForm({
        date: new Date().toISOString().split('T')[0],
        category: '',
        description: '',
        amount: '',
        payment_mode: 'cash',
        paid_to: ''
      });
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await axios.delete(`${API}/cafe/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchDashboard();
      if (activeTab === 'reports') fetchReports();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setEditExpenseForm({
      date: expense.date || '',
      category: expense.category || '',
      description: expense.description || '',
      amount: expense.amount?.toString() || '',
      payment_mode: expense.payment_mode || 'cash',
      paid_to: expense.paid_to || ''
    });
  };

  const handleUpdateExpense = async () => {
    if (!editingExpense) return;
    try {
      await axios.put(`${API}/cafe/expenses/${editingExpense.id}`, {
        ...editExpenseForm,
        amount: parseFloat(editExpenseForm.amount)
      });
      toast.success('Expense updated');
      setEditingExpense(null);
      fetchDashboard();
      if (activeTab === 'reports') fetchReports();
    } catch (error) {
      toast.error('Failed to update expense');
    }
  };

  // Daily Account expense handlers
  const addDailyExpense = () => {
    setDailyAccountForm(prev => ({
      ...prev,
      expenses: [...prev.expenses, {
        id: Date.now(),
        category: '',
        description: '',
        amount: '',
        payment_mode: 'cash',
        paid_to: ''
      }]
    }));
  };

  const updateDailyExpense = (index, field, value) => {
    setDailyAccountForm(prev => {
      const newExpenses = [...prev.expenses];
      newExpenses[index] = { ...newExpenses[index], [field]: value };
      return { ...prev, expenses: newExpenses };
    });
  };

  const removeDailyExpense = (index) => {
    setDailyAccountForm(prev => ({
      ...prev,
      expenses: prev.expenses.filter((_, i) => i !== index)
    }));
  };

  // Calculate turnover: Cash Collected + Online Collected + Cash Expenses Only
  // (Cash collected is net of cash expenses, so we add back only cash expenses for gross turnover)
  const calculateTurnover = () => {
    const cash = parseFloat(dailyAccountForm.cash_collected) || 0;
    const online = parseFloat(dailyAccountForm.online_collected) || 0;
    const cashExpenses = dailyAccountForm.expenses
      .filter(exp => exp.payment_mode === 'cash')
      .reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
    return cash + online + cashExpenses;
  };

  // Calculate only cash expenses for display
  const calculateCashExpenses = () => {
    return dailyAccountForm.expenses
      .filter(exp => exp.payment_mode === 'cash')
      .reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
  };

  // Calculate only online expenses for display
  const calculateOnlineExpenses = () => {
    return dailyAccountForm.expenses
      .filter(exp => exp.payment_mode === 'online')
      .reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
  };

  // Generate PDF for Daily Account Summary
  const generateDailyAccountPDF = (data) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(62, 39, 35); // Primary brown color
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('CAFE 1946', pageWidth / 2, 18, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Daily Account Summary', pageWidth / 2, 28, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Date: ${new Date(data.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth / 2, 36, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    let yPos = 55;
    
    // Collections Section
    doc.setFillColor(232, 245, 233); // Light green
    doc.rect(15, yPos - 5, pageWidth - 30, 30, 'F');
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Collections', 20, yPos + 5);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Cash Collected:`, 25, yPos + 15);
    doc.text(`₹${data.cash_collected.toFixed(2)}`, pageWidth - 25, yPos + 15, { align: 'right' });
    
    doc.text(`Online Collected:`, 25, yPos + 22);
    doc.text(`₹${data.online_collected.toFixed(2)}`, pageWidth - 25, yPos + 22, { align: 'right' });
    
    yPos += 40;
    
    // Expenses Section
    if (data.expenses && data.expenses.length > 0) {
      doc.setFillColor(255, 243, 224); // Light orange
      const expenseHeight = Math.min(data.expenses.length * 12 + 25, 100);
      doc.rect(15, yPos - 5, pageWidth - 30, expenseHeight, 'F');
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Expenses', 20, yPos + 5);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Category', 25, yPos + 15);
      doc.text('Description', 70, yPos + 15);
      doc.text('Mode', 130, yPos + 15);
      doc.text('Amount', pageWidth - 25, yPos + 15, { align: 'right' });
      
      doc.setFont('helvetica', 'normal');
      let expY = yPos + 23;
      
      data.expenses.forEach((exp, index) => {
        if (expY < 250) { // Stay within page bounds
          doc.text(exp.category || '-', 25, expY);
          doc.text((exp.description || '-').substring(0, 25), 70, expY);
          doc.text(exp.payment_mode === 'cash' ? 'Cash' : 'Online', 130, expY);
          doc.text(`₹${parseFloat(exp.amount).toFixed(2)}`, pageWidth - 25, expY, { align: 'right' });
          expY += 10;
        }
      });
      
      yPos += expenseHeight + 10;
    }
    
    // Summary Section
    doc.setFillColor(227, 242, 253); // Light blue
    doc.rect(15, yPos - 5, pageWidth - 30, 50, 'F');
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 20, yPos + 5);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    const cashExp = data.expenses?.filter(e => e.payment_mode === 'cash').reduce((s, e) => s + parseFloat(e.amount || 0), 0) || 0;
    const onlineExp = data.expenses?.filter(e => e.payment_mode === 'online').reduce((s, e) => s + parseFloat(e.amount || 0), 0) || 0;
    
    doc.text(`Total Cash Expenses:`, 25, yPos + 15);
    doc.text(`₹${cashExp.toFixed(2)}`, pageWidth - 25, yPos + 15, { align: 'right' });
    
    doc.text(`Total Online Expenses:`, 25, yPos + 22);
    doc.text(`₹${onlineExp.toFixed(2)}`, pageWidth - 25, yPos + 22, { align: 'right' });
    
    doc.text(`Total Expenses:`, 25, yPos + 29);
    doc.text(`₹${(cashExp + onlineExp).toFixed(2)}`, pageWidth - 25, yPos + 29, { align: 'right' });
    
    // Turnover highlight
    doc.setFillColor(62, 39, 35);
    doc.rect(25, yPos + 33, pageWidth - 50, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`TURNOVER: ₹${data.turnover.toFixed(2)}`, pageWidth / 2, yPos + 40, { align: 'center' });
    
    // Footer
    doc.setTextColor(128, 128, 128);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, pageWidth / 2, 280, { align: 'center' });
    doc.text('Cafe 1946 - South Indian Hotel', pageWidth / 2, 286, { align: 'center' });
    
    return doc;
  };

  const handleDownloadPDF = () => {
    if (!lastSubmittedData) return;
    const doc = generateDailyAccountPDF(lastSubmittedData);
    doc.save(`Cafe1946_DailyAccount_${lastSubmittedData.date}.pdf`);
  };

  const handleSubmitDailyAccount = async (e) => {
    e.preventDefault();
    try {
      // Check if date was changed during editing
      const dateChanged = isEditingDailyAccount && originalEditDate && dailyAccountForm.date !== originalEditDate;
      
      if (dateChanged) {
        // Use the move endpoint to change the date atomically
        try {
          await axios.post(`${API}/cafe/daily-accounts/move`, null, {
            params: {
              original_date: originalEditDate,
              new_date: dailyAccountForm.date
            }
          });
        } catch (moveError) {
          console.error('Error moving entry:', moveError);
          toast.error(moveError.response?.data?.detail || 'Failed to move entry to new date');
          return;
        }
      }
      
      const payload = {
        date: dailyAccountForm.date,
        cash_collected: parseFloat(dailyAccountForm.cash_collected) || 0,
        online_collected: parseFloat(dailyAccountForm.online_collected) || 0,
        expenses: dailyAccountForm.expenses.map(exp => ({
          category: exp.category,
          description: exp.description,
          amount: parseFloat(exp.amount) || 0,
          payment_mode: exp.payment_mode,
          paid_to: exp.paid_to
        }))
      };
      
      const response = await axios.post(`${API}/cafe/daily-accounts`, payload);
      
      // Store submitted data for PDF generation
      const submittedData = {
        ...payload,
        turnover: response.data.turnover
      };
      setLastSubmittedData(submittedData);
      setShowSubmitSuccess(true);
      
      if (dateChanged) {
        toast.success(`Entry moved to ${new Date(dailyAccountForm.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}! Turnover: ₹${response.data.turnover.toFixed(2)}`);
      } else {
        toast.success(`Daily account saved! Turnover: ₹${response.data.turnover.toFixed(2)}`);
      }
      
      // Reset original date tracking
      setOriginalEditDate(null);
      fetchDashboard();
    } catch (error) {
      console.error('Error saving daily account:', error);
      toast.error(error.response?.data?.detail || 'Failed to save daily account');
    }
  };

  const handleNewEntry = () => {
    setShowSubmitSuccess(false);
    setLastSubmittedData(null);
    setIsEditingDailyAccount(false);
    setIsViewMode(false);
    setOriginalEditDate(null);
    setDailyAccountForm({
      date: new Date().toISOString().split('T')[0],
      cash_collected: '',
      online_collected: '',
      expenses: []
    });
  };

  // Enable edit mode from view mode
  const handleEnableEdit = () => {
    setIsViewMode(false);
  };

  // Fetch dates with no data for change date modal
  const fetchAvailableDates = async () => {
    try {
      // Get dates that already have entries from the backend
      const response = await axios.get(`${API}/cafe/daily-accounts/dates-with-entries`);
      const datesWithEntries = new Set(response.data.dates || []);
      
      // Generate last 60 days and filter out dates that have entries
      const dates = [];
      const today = new Date();
      for (let i = 0; i < 60; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        // Exclude current entry's original date and dates that already have data
        if (dateStr !== originalEditDate && !datesWithEntries.has(dateStr)) {
          dates.push(dateStr);
        }
      }
      setAvailableDates(dates);
    } catch (error) {
      console.error('Error fetching available dates:', error);
      toast.error('Failed to load available dates');
    }
  };

  // Handle date change from modal
  const handleChangeDateFromModal = (newDate) => {
    setDailyAccountForm(prev => ({ ...prev, date: newDate }));
    setShowChangeDateModal(false);
    toast.success(`Date changed to ${new Date(newDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}. Save to confirm.`);
  };

  // Load existing daily account when date changes
  const loadDailyAccount = async (date) => {
    try {
      const response = await axios.get(`${API}/cafe/daily-accounts/${date}`);
      if (response.data && response.data.id) {
        setDailyAccountForm({
          date: date,
          cash_collected: response.data.cash_collected ?? '',
          online_collected: response.data.online_collected ?? '',
          expenses: response.data.expenses?.map((exp, i) => ({
            id: i,
            category: exp.category || '',
            description: exp.description || '',
            amount: exp.amount || '',
            payment_mode: exp.payment_mode || 'cash',
            paid_to: exp.paid_to || ''
          })) || []
        });
        setIsEditingDailyAccount(true);
        setIsViewMode(true); // Start in view mode for existing entries
        setOriginalEditDate(date); // Track original date
      } else {
        setDailyAccountForm(prev => ({
          ...prev,
          date: date,
          cash_collected: '',
          online_collected: '',
          expenses: []
        }));
        setIsEditingDailyAccount(false);
        setIsViewMode(false); // New entry - directly in edit mode
        setOriginalEditDate(null);
      }
    } catch (error) {
      console.error('Error loading daily account:', error);
      setIsEditingDailyAccount(false);
      setIsViewMode(false);
      setOriginalEditDate(null);
    }
  };

  // Check if a date has existing entries (for date change validation)
  const checkDateHasEntries = async (date) => {
    try {
      const response = await axios.get(`${API}/cafe/daily-accounts/${date}`);
      return response.data && response.data.id;
    } catch (error) {
      return false;
    }
  };

  // Handle date change when editing an existing entry
  const handleDailyAccountDateChange = async (newDate) => {
    // If not in edit mode or same date, just load normally
    if (!isEditingDailyAccount || !originalEditDate || newDate === originalEditDate) {
      loadDailyAccount(newDate);
      return;
    }

    // Check if new date already has entries
    const hasEntries = await checkDateHasEntries(newDate);
    
    if (hasEntries) {
      // New date has entries - show warning and revert
      toast.error(`Cannot change date: An entry already exists for ${new Date(newDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}. Please select a different date.`);
      // Revert to original date (keep form data unchanged)
      setDailyAccountForm(prev => ({ ...prev, date: originalEditDate }));
    } else {
      // New date is empty - allow the change
      setDailyAccountForm(prev => ({ ...prev, date: newDate }));
      toast.success(`Date changed to ${new Date(newDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}. The entry will be moved to this date when saved.`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Initial Loading Screen */}
      {initialLoading && (
        <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
          <LogoLoader size="lg" text="Loading Expense Tracker..." />
        </div>
      )}
      
      {/* Header */}
      <div className="sticky top-0 z-40 bg-surface border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={onBack} className="p-2">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-primary">Cafe 1946 - Expense Tracker</h1>
                  {/* Environment Badge */}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 ${
                    currentEnvironment === 'prod' 
                      ? 'bg-green-100 text-green-700 border border-green-300' 
                      : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                  }`}>
                    {currentEnvironment === 'prod' ? (
                      <><ShieldCheck className="h-3 w-3" /> PRODUCTION</>
                    ) : (
                      <><FlaskConical className="h-3 w-3" /> TESTING</>
                    )}
                  </span>
                </div>
                <p className="text-sm text-secondary">South Indian Hotel Management</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setIsSettingsOpen(true)} className="gap-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: PieChart },
              { id: 'expense', label: 'Add Expense', icon: Receipt },
              { id: 'daily', label: 'Daily Account', icon: Calendar },
              { id: 'reports', label: 'Reports', icon: BarChart3 }
            ].map(tab => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'ghost'}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 gap-2 ${activeTab === tab.id ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4">
        
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Balance Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Cash Balance</p>
                      <p className="text-2xl font-bold">₹{dashboardData.cash_balance?.toFixed(2) || '0.00'}</p>
                    </div>
                    <Wallet className="h-10 w-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Online Balance</p>
                      <p className="text-2xl font-bold">₹{dashboardData.online_balance?.toFixed(2) || '0.00'}</p>
                    </div>
                    <CreditCard className="h-10 w-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Today&apos;s Turnover</p>
                      <p className="text-2xl font-bold">₹{dashboardData.today_turnover?.toFixed(2) || '0.00'}</p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">Total Expenses</p>
                      <p className="text-2xl font-bold">₹{((dashboardData.total_cash_expenses || 0) + (dashboardData.total_online_expenses || 0)).toFixed(2)}</p>
                    </div>
                    <DollarSign className="h-10 w-10 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-secondary">Cash Collected</p>
                  <p className="text-lg font-semibold text-green-600">₹{dashboardData.total_cash_collected?.toFixed(2) || '0'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-secondary">Online Collected</p>
                  <p className="text-lg font-semibold text-blue-600">₹{dashboardData.total_online_collected?.toFixed(2) || '0'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-secondary">Cash Expenses</p>
                  <p className="text-lg font-semibold text-red-600">₹{dashboardData.total_cash_expenses?.toFixed(2) || '0'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-secondary">Online Expenses</p>
                  <p className="text-lg font-semibold text-red-600">₹{dashboardData.total_online_expenses?.toFixed(2) || '0'}</p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Expenses */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Recent Expenses
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData.recent_expenses?.length > 0 ? (
                  <div className="space-y-2">
                    {dashboardData.recent_expenses.map((expense, index) => (
                      <div key={expense.id || index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{expense.category}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${expense.payment_mode === 'cash' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {expense.payment_mode}
                            </span>
                          </div>
                          <p className="text-xs text-secondary">{expense.description || 'No description'}</p>
                          <p className="text-xs text-secondary">{expense.date}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-red-600">-₹{expense.amount?.toFixed(2)}</span>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-secondary py-8">No recent expenses</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add Expense Tab */}
        {activeTab === 'expense' && (
          <Card className="max-w-xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Update Expense (Day-wise)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateExpense} className="space-y-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Category *</Label>
                  <div className="flex gap-2 mt-1">
                    <select
                      value={expenseForm.category}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, category: e.target.value }))}
                      className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsNewCategoryOpen(true)}
                      className="px-3"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label>Description</Label>
                  <Input
                    placeholder="Enter description"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Amount (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter amount"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Payment Mode *</Label>
                  <select
                    value={expenseForm.payment_mode}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, payment_mode: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="cash">Cash</option>
                    <option value="online">Online</option>
                  </select>
                </div>
                
                <div>
                  <Label>Paid To (Optional)</Label>
                  <Input
                    placeholder="Enter recipient name"
                    value={expenseForm.paid_to}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, paid_to: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                <Button type="submit" className="w-full h-12 text-lg">
                  Save Expense
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Daily Account Tab */}
        {activeTab === 'daily' && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Daily Account Update
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Success State with Download Option */}
              {showSubmitSuccess && lastSubmittedData ? (
                <div className="space-y-6">
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-green-700 mb-2">Successfully Submitted!</h3>
                    <p className="text-secondary mb-4">
                      Daily account for {new Date(lastSubmittedData.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} has been saved.
                    </p>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-purple-700 mb-1">Total Turnover</p>
                      <p className="text-3xl font-bold text-purple-700">₹{lastSubmittedData.turnover.toFixed(2)}</p>
                    </div>
                  </div>
                  
                  {/* Summary Card */}
                  <div className="bg-muted rounded-lg p-4 space-y-3">
                    <h4 className="font-semibold text-primary">Summary</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-secondary">Cash Collected:</span>
                        <span className="font-medium text-green-600">₹{lastSubmittedData.cash_collected.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-secondary">Online Collected:</span>
                        <span className="font-medium text-blue-600">₹{lastSubmittedData.online_collected.toFixed(2)}</span>
                      </div>
                    </div>
                    {lastSubmittedData.expenses?.length > 0 && (
                      <div className="border-t pt-3 mt-3">
                        <p className="text-sm text-secondary mb-2">Expenses ({lastSubmittedData.expenses.length})</p>
                        {lastSubmittedData.expenses.map((exp, i) => (
                          <div key={i} className="flex justify-between text-sm py-1">
                            <span>{exp.category} <span className={`text-xs px-1.5 py-0.5 rounded ${exp.payment_mode === 'cash' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{exp.payment_mode}</span></span>
                            <span className="text-red-600">-₹{exp.amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Download Button */}
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 text-center">
                    <p className="text-amber-800 mb-3">📄 Download your daily account summary as PDF</p>
                    <Button 
                      onClick={handleDownloadPDF}
                      className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Click here to Download PDF
                    </Button>
                  </div>
                  
                  {/* New Entry Button */}
                  <Button 
                    onClick={handleNewEntry}
                    variant="outline"
                    className="w-full h-12"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Daily Account Entry
                  </Button>
                </div>
              ) : (
              <div className="space-y-6">
                {/* Date Selector - Always visible */}
                <div>
                  <Label>Select Date *</Label>
                  <Input
                    type="date"
                    value={dailyAccountForm.date}
                    onChange={(e) => {
                      // When not in view mode and not editing, load the date data
                      if (!isViewMode) {
                        loadDailyAccount(e.target.value);
                      }
                    }}
                    disabled={isViewMode}
                    className="mt-1"
                  />
                </div>

                {/* VIEW MODE - Read-only display */}
                {isViewMode && isEditingDailyAccount ? (
                  <div className="space-y-4">
                    {/* View Mode Header */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <span className="font-medium text-blue-800">
                            Viewing entry for {new Date(dailyAccountForm.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        <Button 
                          onClick={handleEnableEdit}
                          className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
                        >
                          <Edit2 className="h-4 w-4" />
                          Edit
                        </Button>
                      </div>
                    </div>

                    {/* Collections Display */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Wallet className="h-5 w-5 text-green-600" />
                          <span className="text-sm font-medium text-green-800">Cash Collected</span>
                        </div>
                        <p className="text-2xl font-bold text-green-700">₹{parseFloat(dailyAccountForm.cash_collected) || 0}</p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="h-5 w-5 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800">Online Collected</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-700">₹{parseFloat(dailyAccountForm.online_collected) || 0}</p>
                      </div>
                    </div>

                    {/* Expenses Display */}
                    {dailyAccountForm.expenses.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-red-50 px-4 py-3 border-b flex items-center gap-2">
                          <Receipt className="h-5 w-5 text-red-600" />
                          <span className="font-medium text-red-800">Expenses ({dailyAccountForm.expenses.length})</span>
                        </div>
                        <div className="divide-y">
                          {dailyAccountForm.expenses.map((expense, index) => (
                            <div key={index} className="px-4 py-3 flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-primary">{expense.category || 'Uncategorized'}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded ${expense.payment_mode === 'cash' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {expense.payment_mode}
                                  </span>
                                </div>
                                {expense.description && <p className="text-sm text-secondary">{expense.description}</p>}
                                {expense.paid_to && <p className="text-xs text-secondary">Paid to: {expense.paid_to}</p>}
                              </div>
                              <span className="font-bold text-red-600">-₹{parseFloat(expense.amount) || 0}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Turnover Summary */}
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-xl">
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                        <div>
                          <p className="text-purple-200 text-xs">Cash Collected</p>
                          <p className="font-bold">₹{parseFloat(dailyAccountForm.cash_collected) || 0}</p>
                        </div>
                        <div>
                          <p className="text-purple-200 text-xs">Online Collected</p>
                          <p className="font-bold">₹{parseFloat(dailyAccountForm.online_collected) || 0}</p>
                        </div>
                        <div>
                          <p className="text-purple-200 text-xs">Cash Exp.</p>
                          <p className="font-bold text-yellow-300">₹{calculateCashExpenses()}</p>
                        </div>
                        <div>
                          <p className="text-purple-200 text-xs">Online Exp.</p>
                          <p className="font-bold text-blue-300">₹{calculateOnlineExpenses()}</p>
                        </div>
                        <div className="bg-white/20 rounded-lg py-1 col-span-2 sm:col-span-1">
                          <p className="text-purple-200 text-xs">TURNOVER</p>
                          <p className="font-bold text-lg">₹{calculateTurnover()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button 
                        onClick={handleNewEntry}
                        variant="outline"
                        className="flex-1"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Entry
                      </Button>
                      <Button 
                        onClick={handleEnableEdit}
                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit This Entry
                      </Button>
                    </div>
                  </div>
                ) : (
                /* EDIT MODE - Form inputs */
                <form onSubmit={handleSubmitDailyAccount} className="space-y-6">
                {/* Editing Indicator with Change Date option */}
                {isEditingDailyAccount && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Edit2 className="h-4 w-4 text-amber-600" />
                        <span className="text-sm text-amber-800">
                          Editing entry for {new Date(dailyAccountForm.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                      <Button 
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          fetchAvailableDates();
                          setShowChangeDateModal(true);
                        }}
                        className="text-amber-700 border-amber-400 hover:bg-amber-100"
                      >
                        <Calendar className="h-3 w-3 mr-1" />
                        Change Date
                      </Button>
                    </div>
                    {dailyAccountForm.date !== originalEditDate && (
                      <p className="text-xs text-amber-600 mt-2">
                        ⚠️ Date will be changed from {new Date(originalEditDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} to {new Date(dailyAccountForm.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} when saved
                      </p>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-green-600" />
                      Cash Collected (₹)
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Total cash collected"
                      value={dailyAccountForm.cash_collected}
                      onChange={(e) => setDailyAccountForm(prev => ({ ...prev, cash_collected: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-blue-600" />
                      Online Collection (₹)
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Total online collection"
                      value={dailyAccountForm.online_collected}
                      onChange={(e) => setDailyAccountForm(prev => ({ ...prev, online_collected: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                {/* Expenses Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-semibold">Daily Expenses</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addDailyExpense} className="gap-1">
                      <Plus className="h-4 w-4" />
                      Add Expense
                    </Button>
                  </div>
                  
                  {dailyAccountForm.expenses.length > 0 ? (
                    <div className="space-y-4">
                      {dailyAccountForm.expenses.map((expense, index) => (
                        <div key={expense.id} className="p-4 border rounded-lg bg-muted/50 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">Expense #{index + 1}</span>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeDailyExpense(index)}
                              className="text-red-500 hover:text-red-700 p-1 h-auto"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Category</Label>
                              <div className="flex gap-1 mt-1">
                                <Select
                                  value={expense.category}
                                  onValueChange={(value) => updateDailyExpense(index, 'category', value)}
                                >
                                  <SelectTrigger className="flex-1 h-9">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {categories.map(cat => (
                                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setIsNewCategoryOpen(true)}
                                  className="px-2 h-9"
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-xs">Amount (₹)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                value={expense.amount}
                                onChange={(e) => updateDailyExpense(index, 'amount', e.target.value)}
                                className="mt-1 h-9"
                              />
                            </div>
                            
                            <div>
                              <Label className="text-xs">Description</Label>
                              <Input
                                placeholder="Description"
                                value={expense.description}
                                onChange={(e) => updateDailyExpense(index, 'description', e.target.value)}
                                className="mt-1 h-9"
                              />
                            </div>
                            
                            <div>
                              <Label className="text-xs">Payment Mode</Label>
                              <Select
                                value={expense.payment_mode}
                                onValueChange={(value) => updateDailyExpense(index, 'payment_mode', value)}
                              >
                                <SelectTrigger className="mt-1 h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="cash">Cash</SelectItem>
                                  <SelectItem value="online">Online</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-xs">Paid To (Optional)</Label>
                            <Input
                              placeholder="Recipient name"
                              value={expense.paid_to}
                              onChange={(e) => updateDailyExpense(index, 'paid_to', e.target.value)}
                              className="mt-1 h-9"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-secondary py-4 bg-muted rounded-lg">
                      No expenses added. Click &quot;Add Expense&quot; to add.
                    </p>
                  )}
                </div>
                
                {/* Turnover Summary */}
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-xl">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                    <div>
                      <p className="text-purple-200 text-xs">Cash Collected</p>
                      <p className="font-bold">₹{parseFloat(dailyAccountForm.cash_collected) || 0}</p>
                    </div>
                    <div>
                      <p className="text-purple-200 text-xs">Online Collected</p>
                      <p className="font-bold">₹{parseFloat(dailyAccountForm.online_collected) || 0}</p>
                    </div>
                    <div>
                      <p className="text-purple-200 text-xs">Cash Exp.</p>
                      <p className="font-bold text-yellow-300">₹{calculateCashExpenses()}</p>
                    </div>
                    <div>
                      <p className="text-purple-200 text-xs">Online Exp.</p>
                      <p className="font-bold text-blue-300">₹{calculateOnlineExpenses()}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg py-1 col-span-2 sm:col-span-1">
                      <p className="text-purple-200 text-xs">TURNOVER</p>
                      <p className="font-bold text-lg">₹{calculateTurnover()}</p>
                    </div>
                  </div>
                  <p className="text-purple-200 text-xs mt-2 text-center">
                    Formula: Cash + Online + Cash Expenses = Turnover
                  </p>
                </div>
                
                <Button type="submit" className={`w-full h-12 text-lg ${isEditingDailyAccount ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'}`}>
                  {isEditingDailyAccount ? 'Update Daily Account' : 'Submit Daily Account'}
                </Button>
              </form>
              )}
            </div>
            )}
            </CardContent>
          </Card>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Date Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={reportFilters.start_date}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, start_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={reportFilters.end_date}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, end_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <Button onClick={fetchReports}>Generate Report</Button>
                </div>
              </CardContent>
            </Card>

            {reportData && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-secondary">Total Turnover</p>
                      <p className="text-2xl font-bold text-purple-600">₹{reportData.turnover_summary?.total_turnover?.toFixed(2) || '0'}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-secondary">Total Expenses</p>
                      <p className="text-2xl font-bold text-red-600">₹{reportData.total_expenses?.toFixed(2) || '0'}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-green-700 font-medium">Cash Balance</p>
                      <p className="text-2xl font-bold text-green-600">₹{reportData.turnover_summary?.cash_balance?.toFixed(2) || '0'}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-blue-700 font-medium">Digital Balance</p>
                      <p className="text-2xl font-bold text-blue-600">₹{reportData.turnover_summary?.digital_balance?.toFixed(2) || '0'}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Category Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5" />
                      Category-wise Expenses
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reportData.category_breakdown?.length > 0 ? (
                      <div className="space-y-2">
                        {reportData.category_breakdown.map((cat, index) => {
                          const isExpanded = expandedCategories[cat.category];
                          return (
                            <div key={index} className="border rounded-lg overflow-hidden">
                              {/* Category Header - Clickable */}
                              <div 
                                className="flex items-center justify-between p-3 bg-muted cursor-pointer hover:bg-muted/80 transition-colors"
                                onClick={() => setExpandedCategories(prev => ({
                                  ...prev,
                                  [cat.category]: !prev[cat.category]
                                }))}
                              >
                                <div className="flex items-center gap-3">
                                  <div 
                                    className="w-4 h-4 rounded-full"
                                    style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}
                                  />
                                  <span className="font-medium">{cat.category}</span>
                                  <span className="text-xs text-secondary">
                                    ({cat.expenses?.length || 0} expenses)
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="font-bold">₹{cat.amount?.toFixed(2)}</span>
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4 text-secondary" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4 text-secondary" />
                                  )}
                                </div>
                              </div>
                              
                              {/* Expanded Details */}
                              {isExpanded && cat.expenses?.length > 0 && (
                                <div className="border-t bg-white">
                                  {/* Average per day */}
                                  <div className="px-4 py-2 bg-blue-50 text-sm flex justify-between items-center">
                                    <span className="text-blue-700">Average per day ({reportData.period?.num_days || 1} days)</span>
                                    <span className="font-semibold text-blue-700">₹{cat.average_per_day?.toFixed(2) || '0.00'}</span>
                                  </div>
                                  
                                  {/* Day-wise expense list */}
                                  <div className="divide-y">
                                    {cat.expenses.map((exp, expIndex) => (
                                      <div key={expIndex} className="px-4 py-2 flex items-center justify-between text-sm hover:bg-gray-50">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-secondary font-medium">{exp.date}</span>
                                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                                              exp.payment_mode === 'cash' 
                                                ? 'bg-green-100 text-green-700' 
                                                : 'bg-blue-100 text-blue-700'
                                            }`}>
                                              {exp.payment_mode}
                                            </span>
                                          </div>
                                          <p className="text-primary">{exp.description || '-'}</p>
                                          {exp.paid_to && (
                                            <p className="text-xs text-secondary">Paid to: {exp.paid_to}</p>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-semibold text-primary">₹{exp.amount?.toFixed(2)}</span>
                                          {exp.id && (
                                            <>
                                              <button onClick={() => handleEditExpense(exp)} className="p-1 hover:bg-blue-100 rounded text-gray-500 hover:text-blue-700">
                                                <Edit2 className="h-4 w-4" />
                                              </button>
                                              <button onClick={() => handleDeleteExpense(exp.id)} className="p-1 hover:bg-red-100 rounded text-gray-500 hover:text-red-600">
                                                <Trash2 className="h-4 w-4" />
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center text-secondary py-4">No expenses in this period</p>
                    )}
                  </CardContent>
                </Card>

                {/* Payment Mode Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Payment Mode Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div 
                        className="p-4 bg-green-50 rounded-lg text-center cursor-pointer hover:bg-green-100 transition-colors border-2 border-transparent hover:border-green-300"
                        onClick={() => setExpandedPaymentModes(prev => ({ ...prev, cash: !prev.cash }))}
                      >
                        <Wallet className="h-8 w-8 text-green-600 mx-auto mb-2" />
                        <p className="text-sm text-secondary">Cash Expenses</p>
                        <p className="text-xl font-bold text-green-600">₹{reportData.payment_mode_breakdown?.cash?.toFixed(2) || '0'}</p>
                        <p className="text-xs text-green-600 mt-1">Click to {expandedPaymentModes.cash ? 'hide' : 'view'} details</p>
                      </div>
                      <div 
                        className="p-4 bg-blue-50 rounded-lg text-center cursor-pointer hover:bg-blue-100 transition-colors border-2 border-transparent hover:border-blue-300"
                        onClick={() => setExpandedPaymentModes(prev => ({ ...prev, online: !prev.online }))}
                      >
                        <CreditCard className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                        <p className="text-sm text-secondary">Online Expenses</p>
                        <p className="text-xl font-bold text-blue-600">₹{reportData.payment_mode_breakdown?.online?.toFixed(2) || '0'}</p>
                        <p className="text-xs text-blue-600 mt-1">Click to {expandedPaymentModes.online ? 'hide' : 'view'} details</p>
                      </div>
                    </div>
                    
                    {/* Cash Expenses List */}
                    {expandedPaymentModes.cash && (
                      <div className="mb-4 border rounded-lg overflow-hidden">
                        <div className="bg-green-100 px-4 py-2 font-medium text-green-800">
                          Cash Expenses ({reportData.all_expenses?.filter(e => e.payment_mode === 'cash').length || 0})
                        </div>
                        <div className="divide-y max-h-64 overflow-y-auto">
                          {reportData.all_expenses?.filter(e => e.payment_mode === 'cash').map((exp, idx) => (
                            <div key={idx} className="px-4 py-2 flex items-center justify-between text-sm hover:bg-gray-50">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-secondary font-medium">{exp.date}</span>
                                  <span className="text-primary font-medium">{exp.category}</span>
                                </div>
                                <p className="text-gray-600">{exp.description || '-'}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-green-700">₹{exp.amount?.toFixed(2)}</span>
                                {exp.id && (
                                  <>
                                    <button onClick={() => handleEditExpense(exp)} className="p-1 hover:bg-green-100 rounded text-gray-500 hover:text-green-700">
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => handleDeleteExpense(exp.id)} className="p-1 hover:bg-red-100 rounded text-gray-500 hover:text-red-600">
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                          {(!reportData.all_expenses?.filter(e => e.payment_mode === 'cash').length) && (
                            <p className="text-center text-secondary py-4">No cash expenses</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Online Expenses List */}
                    {expandedPaymentModes.online && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-blue-100 px-4 py-2 font-medium text-blue-800">
                          Online Expenses ({reportData.all_expenses?.filter(e => e.payment_mode === 'online').length || 0})
                        </div>
                        <div className="divide-y max-h-64 overflow-y-auto">
                          {reportData.all_expenses?.filter(e => e.payment_mode === 'online').map((exp, idx) => (
                            <div key={idx} className="px-4 py-2 flex items-center justify-between text-sm hover:bg-gray-50">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-secondary font-medium">{exp.date}</span>
                                  <span className="text-primary font-medium">{exp.category}</span>
                                </div>
                                <p className="text-gray-600">{exp.description || '-'}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-blue-700">₹{exp.amount?.toFixed(2)}</span>
                                {exp.id && (
                                  <>
                                    <button onClick={() => handleEditExpense(exp)} className="p-1 hover:bg-blue-100 rounded text-gray-500 hover:text-blue-700">
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => handleDeleteExpense(exp.id)} className="p-1 hover:bg-red-100 rounded text-gray-500 hover:text-red-600">
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                          {(!reportData.all_expenses?.filter(e => e.payment_mode === 'online').length) && (
                            <p className="text-center text-secondary py-4">No online expenses</p>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Daily Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Daily Expense Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reportData.daily_breakdown?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-3 text-sm">Date</th>
                              <th className="text-right py-2 px-3 text-sm">Cash</th>
                              <th className="text-right py-2 px-3 text-sm">Online</th>
                              <th className="text-right py-2 px-3 text-sm">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.daily_breakdown.map((day, index) => (
                              <tr key={index} className="border-b hover:bg-muted/50">
                                <td className="py-2 px-3 text-sm">{day.date}</td>
                                <td className="py-2 px-3 text-sm text-right text-green-600">₹{day.cash?.toFixed(2)}</td>
                                <td className="py-2 px-3 text-sm text-right text-blue-600">₹{day.online?.toFixed(2)}</td>
                                <td className="py-2 px-3 text-sm text-right font-semibold">₹{day.total?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-center text-secondary py-4">No data available</p>
                    )}
                  </CardContent>
                </Card>

                {/* Daily Accounts Summary */}
                {reportData.daily_accounts?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Daily Account Records
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-3 text-sm">Date</th>
                              <th className="text-right py-2 px-3 text-sm">Cash</th>
                              <th className="text-right py-2 px-3 text-sm">Online</th>
                              <th className="text-right py-2 px-3 text-sm">Expenses</th>
                              <th className="text-right py-2 px-3 text-sm">Turnover</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.daily_accounts.map((acc, index) => (
                              <tr key={index} className="border-b hover:bg-muted/50">
                                <td className="py-2 px-3 text-sm">{acc.date}</td>
                                <td className="py-2 px-3 text-sm text-right text-green-600">₹{acc.cash_collected?.toFixed(2)}</td>
                                <td className="py-2 px-3 text-sm text-right text-blue-600">₹{acc.online_collected?.toFixed(2)}</td>
                                <td className="py-2 px-3 text-sm text-right text-red-600">₹{acc.total_expenses?.toFixed(2)}</td>
                                <td className="py-2 px-3 text-sm text-right font-bold text-purple-600">₹{acc.turnover?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* New Category Modal */}
      <Dialog open={isNewCategoryOpen} onOpenChange={setIsNewCategoryOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category Name</Label>
              <Input
                placeholder="Enter category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsNewCategoryOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleCreateCategory} className="flex-1">
                Add Category
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Environment Switch */}
            <div className="p-4 border rounded-lg bg-muted/50">
              <Label className="text-base font-semibold mb-3 block">Environment</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleSwitchEnvironment('dev')}
                  disabled={isSwitchingEnv}
                  className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                    currentEnvironment === 'dev'
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <FlaskConical className={`h-6 w-6 ${currentEnvironment === 'dev' ? 'text-yellow-600' : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium ${currentEnvironment === 'dev' ? 'text-yellow-700' : 'text-gray-600'}`}>
                    Development
                  </span>
                  <span className="text-xs text-gray-500">Testing & Dummy Data</span>
                </button>
                <button
                  onClick={() => handleSwitchEnvironment('prod')}
                  disabled={isSwitchingEnv}
                  className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                    currentEnvironment === 'prod'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <ShieldCheck className={`h-6 w-6 ${currentEnvironment === 'prod' ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium ${currentEnvironment === 'prod' ? 'text-green-700' : 'text-gray-600'}`}>
                    Production
                  </span>
                  <span className="text-xs text-gray-500">Actual Data</span>
                </button>
              </div>
              <p className="text-xs text-secondary mt-3">
                {currentEnvironment === 'dev' 
                  ? '⚠️ You are in testing mode. Data here is for testing purposes only.'
                  : '✅ You are in production mode. Data here is actual business data.'}
              </p>
              
              {/* Flush Data Button - Only for Testing Mode */}
              {currentEnvironment === 'dev' && (
                <Button
                  variant="outline"
                  onClick={handleFlushData}
                  className="w-full mt-3 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Flush All Testing Data
                </Button>
              )}
            </div>

            {/* Opening Balance Settings */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Opening Balances</Label>
              <div>
                <Label className="text-sm">Opening Cash Balance (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Enter opening cash balance"
                  value={settingsForm.opening_cash}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, opening_cash: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Opening Online Balance (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Enter opening online balance"
                  value={settingsForm.opening_online}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, opening_online: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsSettingsOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveSettings} className="flex-1">
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Environment Switch Confirmation Modal */}
      <Dialog open={showEnvConfirm} onOpenChange={setShowEnvConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Switch to Production?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-secondary">
              You are about to switch to <strong>Production</strong> environment. 
              This will show your actual business data.
            </p>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                ⚠️ Make sure you have completed testing before switching to production.
                Changes in production will affect actual records.
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEnvConfirm(false);
                  setPendingEnv(null);
                }} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => confirmSwitchEnvironment(pendingEnv)}
                disabled={isSwitchingEnv}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {isSwitchingEnv ? 'Switching...' : 'Switch to Production'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Modal */}
      <Dialog open={!!editingExpense} onOpenChange={(open) => !open && setEditingExpense(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Edit Expense
            </DialogTitle>
          </DialogHeader>
          {editingExpense && (
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={editExpenseForm.date}
                onChange={(e) => setEditExpenseForm(prev => ({ ...prev, date: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Category</Label>
              <select
                value={editExpenseForm.category}
                onChange={(e) => setEditExpenseForm(prev => ({ ...prev, category: e.target.value }))}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={editExpenseForm.description}
                onChange={(e) => setEditExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={editExpenseForm.amount}
                onChange={(e) => setEditExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Payment Mode</Label>
              <select
                value={editExpenseForm.payment_mode}
                onChange={(e) => setEditExpenseForm(prev => ({ ...prev, payment_mode: e.target.value }))}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="cash">Cash</option>
                <option value="online">Online</option>
              </select>
            </div>
            <div>
              <Label>Paid To</Label>
              <Input
                value={editExpenseForm.paid_to}
                onChange={(e) => setEditExpenseForm(prev => ({ ...prev, paid_to: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingExpense(null)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleUpdateExpense} className="flex-1">
                Save Changes
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Date Modal */}
      <Dialog open={showChangeDateModal} onOpenChange={setShowChangeDateModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-600" />
              Change Entry Date
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-secondary">
              Move this entry from <strong>{originalEditDate && new Date(originalEditDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</strong> to a new date.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                ⚠️ Only dates without existing entries are available. Select from the list below.
              </p>
            </div>

            {availableDates.length > 0 ? (
              <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
                {availableDates.slice(0, 30).map((date) => (
                  <button
                    key={date}
                    onClick={() => handleChangeDateFromModal(date)}
                    className="w-full px-4 py-3 text-left hover:bg-amber-50 transition-colors flex items-center justify-between"
                  >
                    <span className="font-medium text-primary">
                      {new Date(date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">Available</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-secondary">
                <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Loading available dates...</p>
              </div>
            )}

            <Button 
              variant="outline" 
              onClick={() => setShowChangeDateModal(false)} 
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CafeExpenseTracker;
